import { i18n } from '@/i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { procurementApi, type PurchaseRequestResponse } from '@/api/procurement.api';
import type { SelectOption } from '@/components/ui/Select';
import { formatPurchaseRequestLabel } from '@/utils/entityLabels';
import { useDebounce } from '@/hooks/useDebounce';
import { showApiError } from '@/utils/apiError';

function prOption(row: PurchaseRequestResponse): SelectOption {
  return { value: String(row.id), label: formatPurchaseRequestLabel(row) };
}

function mergeSelectedOption(options: SelectOption[], selected?: SelectOption | null): SelectOption[] {
  if (!selected) return options;
  if (options.some((opt) => opt.value === selected.value)) return options;
  return [selected, ...options];
}

type Params = { value?: string; pageSize?: number; requestStatus?: string };

export function usePurchaseRequestSelectSearch({ value = '', pageSize = 30, requestStatus }: Params = {}) {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [rows, setRows] = useState<PurchaseRequestResponse[]>([]);
  const [selected, setSelected] = useState<PurchaseRequestResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  const load = useCallback(
    async (search: string) => {
      setSearching(true);
      try {
        const res = await procurementApi.listPurchaseRequests({
          search: search || undefined,
          requestStatus: requestStatus || undefined,
          page: 0,
          size: pageSize,
        });
        setRows(res.content);
      } catch (err) {
        showApiError(err, i18n.t('errors.failedToLoadPurchaseRequests', { ns: 'common' }));
      } finally {
        setSearching(false);
        setLoading(false);
      }
    },
    [pageSize, requestStatus],
  );

  useEffect(() => {
    void load(debouncedSearch);
  }, [debouncedSearch, load]);

  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    const id = Number(value);
    if (Number.isNaN(id)) return;
    const fromList = rows.find((r) => r.id === id);
    if (fromList) {
      setSelected(fromList);
    }
  }, [value, rows]);

  const options = useMemo(() => {
    const base = rows.map(prOption);
    const sel = selected ? prOption(selected) : null;
    return mergeSelectedOption(base, sel);
  }, [rows, selected]);

  const byId = useMemo(() => {
    const map = new Map<number, PurchaseRequestResponse>();
    for (const r of rows) map.set(r.id, r);
    if (selected) map.set(selected.id, selected);
    return map;
  }, [rows, selected]);

  const getPurchaseRequest = useCallback(
    (id: number | string | null | undefined) => {
      if (id == null || id === '') return undefined;
      return byId.get(Number(id));
    },
    [byId],
  );

  return { options, loading, searching, onSearchQuery: setSearchQuery, getPurchaseRequest };
}
